export interface AttachmentPolicy {
  accept: string;
  multiple?: boolean;
  maxBytes?: number;
  maxFiles?: number;
}

export function acceptsAttachment(file: Pick<File, "name" | "type">, accept: string): boolean {
  return accept.split(",").some((part) => {
    const rule = part.trim().toLowerCase();
    if (!rule) return false;
    if (rule.startsWith(".")) return file.name.toLowerCase().endsWith(rule);
    if (rule.endsWith("/*")) return file.type.toLowerCase().startsWith(rule.slice(0, -1));
    return file.type.toLowerCase() === rule;
  });
}

export function validateAttachments(files: File[], policy: AttachmentPolicy): string | undefined {
  if (!policy.multiple && files.length > 1) return "한 번에 파일 한 개만 첨부해주세요.";
  if (policy.maxFiles && files.length > policy.maxFiles) return `한 번에 최대 ${policy.maxFiles}개까지 첨부할 수 있습니다.`;
  for (const file of files) {
    if (!acceptsAttachment(file, policy.accept)) return `${file.name}: 이 화면에서 지원하지 않는 파일 형식입니다.`;
    if (!file.size) return `${file.name}: 빈 파일은 첨부할 수 없습니다.`;
    if (policy.maxBytes && file.size > policy.maxBytes) return `${file.name}: 파일당 최대 ${policy.maxBytes / 1024 / 1024}MB까지 첨부할 수 있습니다.`;
  }
}

export function mergeAttachmentFiles(current: File[], incoming: File[]): File[] {
  const result = [...current];
  for (const file of incoming) {
    if (!result.some((existing) => existing.name === file.name && existing.size === file.size &&
      existing.type === file.type && existing.lastModified === file.lastModified)) result.push(file);
  }
  return result;
}

/** Only explicit image clipboard items are read; ordinary pasted text stays text. */
export async function readClipboardImages(clipboard: Pick<Clipboard, "read"> | undefined): Promise<File[]> {
  if (!clipboard?.read) throw new Error("이 브라우저에서는 버튼으로 붙여넣을 수 없습니다. 첨부 영역에서 ⌘V 또는 Ctrl+V를 눌러주세요.");
  let items: ClipboardItems;
  try {
    items = await clipboard.read();
  } catch {
    throw new Error("클립보드를 읽지 못했습니다. 이미지 복사 후 첨부 영역에서 ⌘V 또는 Ctrl+V를 누르거나 파일을 선택해주세요.");
  }
  const files: File[] = [];
  for (const item of items) {
    const type = item.types.find((candidate) => candidate === "image/png") ?? item.types.find((candidate) => candidate.startsWith("image/"));
    if (!type) continue;
    const blob = await item.getType(type);
    const extension = type.split("/")[1].replace("jpeg", "jpg").replace(/[^a-z0-9]/gi, "");
    files.push(new File([blob], `붙여넣기-${Date.now()}-${files.length + 1}.${extension}`, { type }));
  }
  if (!files.length) throw new Error("클립보드에 이미지가 없습니다. 사진이나 화면 캡처를 복사해주세요. 문서는 파일 선택으로 첨부할 수 있습니다.");
  return files;
}
